/**
 * 新規予約画面(Phase「LIFF frontend実装」)。
 *
 * 正本(実装指示書 v2.0 12章「新規予約」):
 * 1. 日時選択 2. 顧客情報入力 3. 内容・料金確認 4. Stripe決済
 * 必須：氏名、電話番号、メール、日時、利用時間、注意事項同意。
 * 任意：利用目的、備考。
 * 料金はブラウザ値を信用せずWorkers側で再計算。
 *
 * 重要な設計方針:
 * - 日時選択画面で表示する金額は pricing-estimate.ts による「概算」のみ。
 *   実際の予約作成(POST /v1/bookings)が返す`amount`だけを正式金額として
 *   確認画面・決済画面に表示する。
 * - 空き判定・予約可否はこの画面では一切判定しない。POST /v1/bookingsが
 *   409(booking_conflict / calendar_conflict / hold_conflict)を返した
 *   場合はそのままエラーとして表示し、勝手にリトライ・別解釈をしない。
 * - HOLD取得(POST /v1/bookings)成功後、決済(PaymentIntent作成→Stripe
 *   確定)まで進まずに離脱した場合、HOLDは15分で自動的に失効する
 *   (Durable Object側の既存TTL)。明示的な解放はこの画面では行わない
 *   (キャンセルボタン等の明示操作がないため)。
 *
 * iPhone/LINE LIFF実機確認で発見・是正(Phase 11):
 * - 開始時刻・利用時間は`<input type="time">`/`<input type="number">`の
 *   端末依存ネイティブUIだと任意の分・自由数値(例: 17:44)を作れてしまう
 *   ため、booking-form-options.tsの選択式候補(select)へ変更した。
 * - 利用日は`<input type="date">`だとiOSネイティブの日付ホイールで
 *   「予約可能範囲外」「既に予約済みの日」を個別に区別して不可表示に
 *   できず、不正な日付のまま次画面へ進めてしまう不具合があったため、
 *   calendar-widget.tsによる自前の月表示カレンダーへ置き換えた
 *   (Part D/V)。GET /v1/availability/datesから取得した予約済み日を
 *   赤い×付きdisabledとして表示するが、これは事前案内に過ぎず、
 *   「次へ」進む直前にも既存のJS側バリデーション(isBookableDate)を
 *   必ず通す。
 * - いずれもUI側の最小限の防御であり、最終的な受付可否は既存どおり
 *   POST /v1/bookingsのサーバー側Rule Engineが決定する(このファイルの
 *   判定ロジックを変更しても、サーバー側バリデーションには一切触れない)。
 *
 * Phase 11続行分「祝日をカレンダーUIでも正しく反映」:
 * - GET /v1/availability/datesのholiday_dates(backend側Google Calendar
 *   「日本の祝日」判定結果)をそのまま使う。frontend側で祝日を独自推測・
 *   計算しない。土日祝1日貸し固定UIへの切替判定はisWeekendOrHolidayDate
 *   (booking-form-options.ts)を新規予約・変更申請で共有する。
 * - 利用日を選び直すたびに利用時間を1時間へリセットする(平日の利用時間
 *   初期値を1時間にする、Part 4)。以前は土日祝(duration=13固定)から
 *   平日へ選び直した際、13が平日でも有効な選択肢のため残ってしまう
 *   不具合があった。
 */

import type { AppContext } from "../app-context.js";
import { ApiError } from "../api-client.js";
import { estimatePriceYen } from "../pricing-estimate.js";
import { escapeHtml } from "../dom-utils.js";
import { renderCalendarWidget } from "../calendar-widget.js";
import type { NewBookingConfirmation } from "../types.js";
import {
  WEEKDAY_START_TIME_OPTIONS,
  WEEKEND_HOLIDAY_START_TIME,
  WEEKEND_HOLIDAY_DURATION_HOURS,
  durationOptionsForStartTime,
  computeEndTime,
  ARRIVAL_TIME_OPTIONS,
  bookableDateRange,
  isBookableDate,
  isWeekendOrHolidayDate,
} from "../booking-form-options.js";

/** 現地払い（現金・PayPay） Step 2、2026-09-27追加指示。既定値"card"は既存のカード決済フロー。 */
type PaymentType = "card" | "onsite";

interface WizardState {
  use_date: string;
  start_time: string;
  duration: number;
  name: string;
  phone: string;
  email: string;
  purpose: string;
  notes: string;
  /** 土日祝のみ必須の来館時刻(HH:00)。平日は空(開始時刻=来館時刻)。 */
  arrival_time: string;
  terms_consent: boolean;
  payment_type: PaymentType;
  /** 現地払いのみ必須(カード決済フローでは収集しない)。 */
  address: string;
}

function monthBoundsIso(year: number, month: number): { start: string; end: string } {
  const pad = (n: number) => String(n).padStart(2, "0");
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return { start: `${year}-${pad(month)}-01`, end: `${year}-${pad(month)}-${pad(lastDay)}` };
}

function renderStep1DateTime(root: HTMLElement, ctx: AppContext, state: WizardState): void {
  // 当日予約不可・最大3か月先まで(正本5章)。Asia/Tokyo基準で算出する。
  const { min: minDate, max: maxDate } = bookableDateRange();

  // 既に利用日が選択済み(戻る操作等)ならその月を、未選択ならmin(翌日)の月を初期表示する。
  const initialAnchor = state.use_date && state.use_date >= minDate && state.use_date <= maxDate ? state.use_date : minDate;
  const [anchorYear, anchorMonth] = initialAnchor.split("-").map(Number);
  const calendarState = { year: anchorYear ?? 2026, month: anchorMonth ?? 1 };
  let unavailableDates = new Set<string>();
  let holidayDates = new Set<string>();
  let calendarCheckFailed = false;

  root.innerHTML = `
    <h2>新規予約(1/4: 日時選択)</h2>
    <p class="field-hint" id="onsite-entry-hint">
      ${
        state.payment_type === "onsite"
          ? `<span class="badge">現地払い（現金・PayPay）でお申し込み中</span> `
          : ""
      }
      <button type="button" class="secondary" data-action="onsite-entry">
        ${state.payment_type === "onsite" ? "カード決済に戻す" : "現地払い（現金・PayPay）で予約する"}
      </button>
    </p>
    <div class="field">
      <span id="use-date-label" class="field-label">利用日</span>
      <div id="calendar-container" aria-labelledby="use-date-label"></div>
      <p class="field-hint">当日のご予約はできません。ご利用日の前日までにお申し込みください(最大3か月先まで)。赤い×は既にご予約が入っている予約不可日です。</p>
      <p id="date-error" class="error"></p>
    </div>
    <div id="daytype-section"></div>
    <p class="price-estimate" id="price-estimate"></p>
    <button type="button" data-action="next">次へ(内容入力)</button>
  `;

  root.querySelector('[data-action="onsite-entry"]')?.addEventListener("click", () => {
    state.payment_type = state.payment_type === "onsite" ? "card" : "onsite";
    renderStep1DateTime(root, ctx, state);
  });

  const calendarContainer = root.querySelector("#calendar-container") as HTMLElement;

  const paintCalendar = () => {
    renderCalendarWidget(calendarContainer, {
      year: calendarState.year,
      month: calendarState.month,
      minDate,
      maxDate,
      selectedDate: state.use_date,
      unavailableDates,
      holidayDates,
      calendarCheckFailed,
      onSelectDate: (iso) => {
        state.use_date = iso;
        state.duration = 1; // 利用日を選び直すたびに利用時間を1時間へリセットする(Part 4)。
        (root.querySelector("#date-error") as HTMLElement).textContent = "";
        paintCalendar();
        renderDaytypeSection();
      },
      onMonthChange: (year, month) => {
        calendarState.year = year;
        calendarState.month = month;
        paintCalendar();
        void loadUnavailableDatesForVisibleMonth();
      },
    });
  };

  // 表示中の月をカバーするunavailable_dates/holiday_datesを取得する。
  // 画面を開いた時・対象月を変えた時に最新化する(過剰pollingはしない、
  // 要件どおり)。取得に失敗しても、事前案内が出せないだけで予約自体は
  // ブロックしない(最終防御は既存のサーバー側occupancy再判定・isHoliday())。
  const loadUnavailableDatesForVisibleMonth = async () => {
    const { start, end } = monthBoundsIso(calendarState.year, calendarState.month);
    const fromDate = start < minDate ? minDate : start;
    const toDate = end > maxDate ? maxDate : end;
    if (fromDate > toDate) {
      unavailableDates = new Set();
      holidayDates = new Set();
      calendarCheckFailed = false;
      paintCalendar();
      return;
    }
    try {
      const result = await ctx.api.getUnavailableDates(fromDate, toDate);
      unavailableDates = new Set(result.unavailable_dates);
      holidayDates = new Set(result.holiday_dates);
      calendarCheckFailed = result.calendar_check_ok === false;
    } catch {
      unavailableDates = new Set();
      holidayDates = new Set();
      calendarCheckFailed = true; // 取得できない場合は予約可能と判断しない
    }
    paintCalendar();
    renderDaytypeSection();
  };

  paintCalendar();
  void loadUnavailableDatesForVisibleMonth();

  const updateEstimate = () => {
    const estimateEl = root.querySelector("#price-estimate") as HTMLElement;
    if (!state.use_date || !isBookableDate(state.use_date)) {
      estimateEl.textContent = "";
      return;
    }
    const amount = estimatePriceYen(state.use_date, state.duration);
    estimateEl.innerHTML = `概算 ${amount.toLocaleString("ja-JP")}円<span class="caption">実際の金額は次の画面でサーバーが確定します</span>`;
  };

  const renderDaytypeSection = () => {
    const section = root.querySelector("#daytype-section") as HTMLElement;
    if (!state.use_date || !isBookableDate(state.use_date)) {
      section.innerHTML = "";
      updateEstimate();
      return;
    }

    if (isWeekendOrHolidayDate(state.use_date, holidayDates)) {
      state.start_time = WEEKEND_HOLIDAY_START_TIME;
      state.duration = WEEKEND_HOLIDAY_DURATION_HOURS;
      section.innerHTML = `
        <div class="field">
          <p class="fixed-slot"><strong>土日祝は1日貸し(10:00〜23:00 / 22,000円)です。</strong></p>
        </div>
        <div class="field">
          <label for="arrival-time">来館時刻(必須)</label>
          <select id="arrival-time">
            <option value="">選択してください</option>
            ${ARRIVAL_TIME_OPTIONS.map(
              (t) => `<option value="${t}" ${t === state.arrival_time ? "selected" : ""}>${t}</option>`,
            ).join("")}
          </select>
          <p class="field-hint">ご来館時刻にスタッフが伺い、解錠・施設のご案内を行います。1時間刻みでお選びください。</p>
        </div>
      `;
      root.querySelector("#arrival-time")?.addEventListener("change", (e) => {
        state.arrival_time = (e.target as HTMLSelectElement).value;
        (root.querySelector("#date-error") as HTMLElement).textContent = "";
      });
      updateEstimate();
      return;
    }

    const durationOptions = durationOptionsForStartTime(state.start_time);
    if (!durationOptions.includes(state.duration)) {
      state.duration = durationOptions[0] ?? 1;
    }
    state.arrival_time = "";
    section.innerHTML = `
      <div class="field">
        <label for="start-time">開始時刻</label>
        <select id="start-time">
          ${WEEKDAY_START_TIME_OPTIONS.map(
            (t) => `<option value="${t}" ${t === state.start_time ? "selected" : ""}>${t}</option>`,
          ).join("")}
        </select>
      </div>
      <div class="field">
        <label for="duration">利用時間</label>
        <select id="duration">
          ${durationOptions
            .map((d) => `<option value="${d}" ${d === state.duration ? "selected" : ""}>${d}時間</option>`)
            .join("")}
        </select>
        <p class="field-hint">平日は1〜13時間(10時間以上は上限22,000円)。</p>
        <p class="field-hint">ご予約の開始時刻にスタッフが伺い、解錠・施設のご案内を行います。開始時刻にお越しください。</p>
      </div>
    `;
    root.querySelector("#start-time")?.addEventListener("change", (e) => {
      state.start_time = (e.target as HTMLSelectElement).value;
      renderDaytypeSection();
    });
    root.querySelector("#duration")?.addEventListener("change", (e) => {
      state.duration = Number((e.target as HTMLSelectElement).value);
      updateEstimate();
    });
    updateEstimate();
  };

  const validateDate = (): boolean => {
    const dateErrorEl = root.querySelector("#date-error") as HTMLElement;
    if (!state.use_date) {
      dateErrorEl.textContent = "";
      return false;
    }
    if (!isBookableDate(state.use_date)) {
      dateErrorEl.textContent = "選択された利用日はご予約いただけません(当日・過去日・3か月より先の日付は選べません)。";
      return false;
    }
    dateErrorEl.textContent = "";
    return true;
  };

  root.querySelector('[data-action="next"]')?.addEventListener("click", () => {
    if (!state.use_date) {
      (root.querySelector("#date-error") as HTMLElement).textContent = "利用日を選択してください。";
      return;
    }
    if (!validateDate()) {
      return;
    }
    if (isWeekendOrHolidayDate(state.use_date, holidayDates) && !state.arrival_time) {
      (root.querySelector("#date-error") as HTMLElement).textContent = "来館時刻を選択してください。";
      return;
    }
    renderStep2CustomerInfo(root, ctx, state);
  });

  renderDaytypeSection();
}

function renderStep2CustomerInfo(root: HTMLElement, ctx: AppContext, state: WizardState): void {
  const isOnsite = state.payment_type === "onsite";
  root.innerHTML = `
    <h2>新規予約(2/4: お客様情報)</h2>
    <div class="field">
      <label for="name">氏名(必須)</label>
      <input type="text" id="name" value="${escapeHtml(state.name)}" required />
    </div>
    <div class="field">
      <label for="phone">電話番号(必須)</label>
      <input type="tel" id="phone" value="${escapeHtml(state.phone)}" required />
    </div>
    <div class="field">
      <label for="email">メールアドレス(必須)</label>
      <input type="email" id="email" value="${escapeHtml(state.email)}" required />
    </div>
    ${
      isOnsite
        ? `<div class="field">
             <label for="address">ご住所(必須、現地払いのみ)</label>
             <input type="text" id="address" value="${escapeHtml(state.address)}" required />
           </div>`
        : ""
    }
    <div class="field">
      <label for="purpose">利用目的(任意)</label>
      <input type="text" id="purpose" value="${escapeHtml(state.purpose)}" />
    </div>
    <div class="field">
      <label for="notes">備考(任意)</label>
      <textarea id="notes" rows="3">${escapeHtml(state.notes)}</textarea>
    </div>
    <div class="field">
      <label><input type="checkbox" id="terms-consent" ${state.terms_consent ? "checked" : ""} /> 注意事項に同意します(必須)</label>
    </div>
    <button type="button" data-action="next">次へ(内容確認)</button>
    <button type="button" class="secondary" data-action="back">戻る</button>
  `;

  root.querySelector('[data-action="back"]')?.addEventListener("click", () => renderStep1DateTime(root, ctx, state));
  root.querySelector('[data-action="next"]')?.addEventListener("click", () => {
    const name = (root.querySelector("#name") as HTMLInputElement).value.trim();
    const phone = (root.querySelector("#phone") as HTMLInputElement).value.trim();
    const email = (root.querySelector("#email") as HTMLInputElement).value.trim();
    const address = isOnsite ? (root.querySelector("#address") as HTMLInputElement).value.trim() : "";
    const purpose = (root.querySelector("#purpose") as HTMLInputElement).value.trim();
    const notes = (root.querySelector("#notes") as HTMLTextAreaElement).value.trim();
    const termsConsent = (root.querySelector("#terms-consent") as HTMLInputElement).checked;

    if (!name || !phone || !email) {
      window.alert("氏名・電話番号・メールアドレスは必須です。");
      return;
    }
    if (isOnsite && !address) {
      window.alert("現地払いのお申し込みには住所の入力が必須です。");
      return;
    }
    if (!termsConsent) {
      window.alert("注意事項への同意が必要です。");
      return;
    }

    state.name = name;
    state.phone = phone;
    state.email = email;
    state.address = address;
    state.purpose = purpose;
    state.notes = notes;
    state.terms_consent = termsConsent;
    renderStep3Confirm(root, ctx, state);
  });
}

function renderStep3Confirm(root: HTMLElement, ctx: AppContext, state: WizardState): void {
  const endTime = computeEndTime(state.start_time, state.duration);
  root.innerHTML = `
    <h2>新規予約(3/4: 内容確認)</h2>
    <div class="card">
      <p>利用日: ${escapeHtml(state.use_date)}</p>
      <p>利用時間: ${escapeHtml(state.start_time)}〜${escapeHtml(endTime)}(${state.duration}時間)</p>
      ${state.arrival_time ? `<p>来館時刻: ${escapeHtml(state.arrival_time)}(スタッフが解錠・施設のご案内を行います)</p>` : ""}
      <p>氏名: ${escapeHtml(state.name)}</p>
      <p>電話番号: ${escapeHtml(state.phone)}</p>
      <p>メール: ${escapeHtml(state.email)}</p>
      ${state.payment_type === "onsite" ? `<p>ご住所: ${escapeHtml(state.address)}</p>` : ""}
      ${state.purpose ? `<p>利用目的: ${escapeHtml(state.purpose)}</p>` : ""}
      ${state.notes ? `<p>備考: ${escapeHtml(state.notes)}</p>` : ""}
      <p>お支払い方法: ${state.payment_type === "onsite" ? "現地払い(現金・PayPay)" : "カード決済"}</p>
    </div>
    <p class="field-hint">この内容で仮予約(15分間の枠確保)を行います。正式な金額は次の画面で確定します。</p>
    <button type="button" data-action="submit">この内容で仮予約する</button>
    <button type="button" class="secondary" data-action="back">戻る</button>
    <p id="submit-error" class="error"></p>
  `;

  root.querySelector('[data-action="back"]')?.addEventListener("click", () => renderStep2CustomerInfo(root, ctx, state));
  root.querySelector('[data-action="submit"]')?.addEventListener("click", async () => {
    const submitBtn = root.querySelector('[data-action="submit"]') as HTMLButtonElement;
    submitBtn.disabled = true;
    try {
      const confirmation = await ctx.api.createBooking({
        name: state.name,
        phone: state.phone,
        email: state.email,
        use_date: state.use_date,
        start_time: state.start_time,
        end_time: endTime,
        duration: state.duration,
        terms_consent: true,
        purpose: state.purpose || undefined,
        notes: state.notes || undefined,
      });
      renderStep4Payment(root, ctx, state, confirmation);
    } catch (error) {
      submitBtn.disabled = false;
      const errorEl = root.querySelector("#submit-error") as HTMLElement;
      if (error instanceof ApiError && (error.code === "booking_conflict" || error.code === "calendar_conflict" || error.code === "hold_conflict")) {
        errorEl.textContent = "あいにく、その日程は既にご予約が入っています。別の日時をお選びください。";
      } else if (error instanceof ApiError) {
        errorEl.textContent = `予約の受付に失敗しました(${error.code})。内容をご確認のうえ再度お試しください。`;
      } else {
        errorEl.textContent = "予約の受付に失敗しました。時間をおいて再度お試しください。";
      }
    }
  });
}

function renderStep4Payment(
  root: HTMLElement,
  ctx: AppContext,
  state: WizardState,
  booking: NewBookingConfirmation,
): void {
  if (state.payment_type === "onsite") {
    renderStep4OnsitePayment(root, ctx, state, booking);
    return;
  }
  root.innerHTML = `
    <h2>新規予約(4/4: お支払い)</h2>
    <div class="card">
      <p>ご予約金額: <strong>${booking.amount.toLocaleString("ja-JP")}円</strong></p>
      <p class="field-hint">予約ID: ${escapeHtml(booking.booking_id)}</p>
    </div>
    <div id="payment-element"></div>
    <p id="payment-error" class="error"></p>
    <button type="button" data-action="pay">支払いを確定する</button>
  `;

  (async () => {
    try {
      const paymentIntent = await ctx.api.createPaymentIntent({
        booking_id: booking.booking_id,
        hold_id: booking.hold_id,
        use_date: booking.use_date,
        name: state.name,
        phone: state.phone,
        email: state.email,
        purpose: state.purpose || undefined,
        notes: state.notes || undefined,
        arrival_time: state.arrival_time || undefined,
        terms_consent: true,
      });

      const stripe = ctx.createStripeClient();
      const paymentElement = await stripe.mountPaymentElement(paymentIntent.client_secret, "payment-element");

      root.querySelector('[data-action="pay"]')?.addEventListener("click", async () => {
        const payBtn = root.querySelector('[data-action="pay"]') as HTMLButtonElement;
        payBtn.disabled = true;
        const result = await paymentElement.confirmPayment();
        if (result.ok) {
          root.innerHTML = `
            <h2>お支払いが完了しました</h2>
            <p>予約の確定処理を進めています。数分後に「予約を確認する」画面でご確認いただけます。</p>
            <button type="button" data-action="to-my-bookings">予約を確認する</button>
          `;
          root.querySelector('[data-action="to-my-bookings"]')?.addEventListener("click", () => ctx.navigate("my-bookings"));
        } else {
          payBtn.disabled = false;
          (root.querySelector("#payment-error") as HTMLElement).textContent = result.message;
        }
      });
    } catch {
      (root.querySelector("#payment-error") as HTMLElement).textContent =
        "決済の準備に失敗しました。時間をおいて再度お試しください(予約枠は15分間保持されます)。";
    }
  })();
}

/** 現地払い（現金・PayPay） Step 2、2026-09-27追加指示。Stripeを通さず即時確定する。 */
function renderStep4OnsitePayment(
  root: HTMLElement,
  ctx: AppContext,
  state: WizardState,
  booking: NewBookingConfirmation,
): void {
  root.innerHTML = `
    <h2>新規予約(4/4: 現地払いのご確認)</h2>
    <div class="card">
      <p>ご予約金額: <strong>${booking.amount.toLocaleString("ja-JP")}円</strong>(現地払い: 現金またはPayPay)</p>
      <p class="field-hint">予約ID: ${escapeHtml(booking.booking_id)}</p>
    </div>
    <p id="onsite-error" class="error"></p>
    <button type="button" data-action="confirm-onsite">この内容で予約を確定する</button>
  `;

  root.querySelector('[data-action="confirm-onsite"]')?.addEventListener("click", async () => {
    const confirmBtn = root.querySelector('[data-action="confirm-onsite"]') as HTMLButtonElement;
    confirmBtn.disabled = true;
    try {
      await ctx.api.confirmOnsiteBooking({
        booking_id: booking.booking_id,
        hold_id: booking.hold_id,
        use_date: booking.use_date,
        name: state.name,
        phone: state.phone,
        email: state.email,
        address: state.address,
        purpose: state.purpose || undefined,
        notes: state.notes || undefined,
        arrival_time: state.arrival_time || undefined,
        terms_consent: true,
      });
      root.innerHTML = `
        <h2>ご予約を受け付けました</h2>
        <p>当日は現地にて現金またはPayPayでお支払いください。予約の確定処理を進めています。数分後に「予約を確認する」画面でご確認いただけます。</p>
        <button type="button" data-action="to-my-bookings">予約を確認する</button>
      `;
      root.querySelector('[data-action="to-my-bookings"]')?.addEventListener("click", () => ctx.navigate("my-bookings"));
    } catch (error) {
      confirmBtn.disabled = false;
      const errorEl = root.querySelector("#onsite-error") as HTMLElement;
      if (error instanceof ApiError && (error.code === "hold_expired" || error.code === "hold_id_mismatch" || error.code === "booking_id_mismatch")) {
        errorEl.textContent = "予約枠の確保が期限切れになりました。お手数ですが最初からやり直してください。";
      } else if (error instanceof ApiError) {
        errorEl.textContent = `予約の確定に失敗しました(${error.code})。内容をご確認のうえ再度お試しください。`;
      } else {
        errorEl.textContent = "予約の確定に失敗しました。時間をおいて再度お試しください。";
      }
    }
  });
}

export function renderNewBookingScreen(root: HTMLElement, ctx: AppContext): void {
  const state: WizardState = {
    use_date: "",
    start_time: "10:00",
    duration: 1,
    name: "",
    phone: "",
    email: "",
    purpose: "",
    notes: "",
    arrival_time: "",
    terms_consent: false,
    payment_type: "card",
    address: "",
  };
  renderStep1DateTime(root, ctx, state);
}
